/**
 * AccordionVariantUpdater
 * 
 * Handles automatic updating of product accordion content when product variants change.
 * Listens for variant change events, fetches updated section HTML, and rebinds all
 * event listeners to maintain accordion and lightbox functionality.
 * 
 * Features:
 * - Automatic variant change detection via productVariantUpdated events
 * - Section HTML fetching and replacement
 * - Accordion state preservation (which panels are open)
 * - Complete event listener rebinding for accordion and image lightbox functionality
 * - Robust error handling and section ID detection
 */
class AccordionVariantUpdater {
  constructor() {
    this.accordionContainer = null;
    this.currentSectionId = null;
    this.isUpdating = false;
    this.accordionResizeObserver = null;
    this.accordionImageLoadTimeout = null;
    this.accordionHeightFrameIds = new WeakMap();

    this.init();
  }

  /**
   * Initialize the updater
   * Handles both immediate setup and deferred setup for DOM readiness
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Main setup method
   * Finds the accordion container, gets section ID, and sets up event listeners
   */
  setup() {
    this.accordionContainer = document.getElementById('accordion-container');
    if (!this.accordionContainer) return;

    // Get section ID from accordion container or nearby elements
    this.currentSectionId = this.getSectionId();

    // Initialize accordion functionality on page load
    this.initializeAccordionOnLoad();

    // Listen for variant changes
    document.addEventListener('productVariantUpdated', (event) => {
      this.handleVariantChange(event.detail);
    });
  }

  /**
   * Get the current section ID from various sources
   * Uses multiple fallback methods to reliably find the section ID
   * 
   * @returns {string|null} The section ID or null if not found
   */
  getSectionId() {
    // Try to find section ID from various sources
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects && variantSelects.dataset.section) {
      return variantSelects.dataset.section;
    }
    const sectionElement = document.querySelector('[data-section-id]');
    if (sectionElement) {
      return sectionElement.dataset.sectionId;
    }

    return null;
  }

  getProductFetchUrl() {
    const variantSelects = document.querySelector('variant-selects');
    return variantSelects?.dataset?.url || window.location.pathname;
  }

  /**
   * Handle variant change events
   * Prevents multiple simultaneous updates and triggers accordion update
   * 
   * @param {Object} variantData - The variant data from the productVariantUpdated event
   */
  async handleVariantChange(variantData) {
    if (!this.currentSectionId || this.isUpdating) return;
    if (!variantData.variantId) return;
    if (variantData.skipAccordionSectionFetch || variantData.accordionUpdated) return;

    this.isUpdating = true;

    try {
      await this.updateAccordion(variantData.variantId);
    } catch (error) {
      console.error('Error updating accordion:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Replace accordion HTML from an already-fetched section document (no extra network).
   * @param {Document} doc - Parsed section HTML from VariantSelects section fetch
   */
  applyAccordionFromDocument(doc) {
    const newAccordionContainer = doc.getElementById('accordion-container');
    if (!newAccordionContainer || !this.accordionContainer) return;

    const currentState = this.getCurrentAccordionState();
    this.disconnectAccordionHeightRefresh();
    this.accordionContainer.innerHTML = newAccordionContainer.innerHTML;
    this.reinitializeAccordion();
    this.restoreAccordionState(currentState);
  }

  /**
   * Update accordion content with new variant data
   * Fetches the updated section HTML and replaces accordion content with smooth fade transition
   * 
   * @param {string} variantId - The ID of the selected variant
   */
  async updateAccordion(variantId) {
    this.disconnectAccordionHeightRefresh();
    try {
      // Fetch the updated section HTML
      const response = await fetch(
        `${this.getProductFetchUrl()}?variant=${variantId}&section_id=${this.currentSectionId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the new accordion container in the fetched HTML
      const newAccordionContainer = doc.getElementById('accordion-container');

      if (newAccordionContainer && this.accordionContainer) {
        // Store current state before replacing
        const currentState = this.getCurrentAccordionState();

        // Add fade-out transition
        this.accordionContainer.style.opacity = '0';
        this.accordionContainer.style.transition = 'opacity 150ms ease-out';

        // Wait for fade-out, then replace HTML
        await new Promise(resolve => setTimeout(resolve, 150));

        // Replace the accordion HTML in a single reflow to minimize layout thrashing
        requestAnimationFrame(() => {
          this.accordionContainer.innerHTML = newAccordionContainer.innerHTML;

          // Reinitialize accordion functionality
          this.reinitializeAccordion();

          // Restore accordion state
          this.restoreAccordionState(currentState);

          // Fade back in
          requestAnimationFrame(() => {
            this.accordionContainer.style.opacity = '1';
            this.accordionContainer.style.transition = 'opacity 150ms ease-in';
          });
        });

        // Cleanup transition styles after animation completes
        setTimeout(() => {
          this.accordionContainer.style.transition = '';
          this.accordionContainer.style.opacity = '';
        }, 300);
      }
    } catch (error) {
      console.error('Failed to update accordion:', error);
      // Ensure accordion is visible even if update fails
      this.accordionContainer.style.opacity = '1';
      this.accordionContainer.style.transition = '';
    }
  }

  /**
   * Capture the current state of the accordion
   * Records which panels are expanded, extended description state, and scroll position
   * 
   * @returns {Object} State object containing expanded panels and description state
   */
  getCurrentAccordionState() {
    const state = {
      expandedPanels: [],
      extendedDescriptionExpanded: false,
      scrollPosition: window.scrollY || window.pageYOffset
    };

    // Check which panels are expanded
    const headers = this.accordionContainer.querySelectorAll('.accordion-header');
    headers.forEach((header, index) => {
      if (header.getAttribute('aria-expanded') === 'true') {
        state.expandedPanels.push(index);
      }
    });

    // Check if extended description is expanded
    const extendedToggle = this.accordionContainer.querySelector('#extendedDescriptionToggle');
    if (extendedToggle) {
      const arrowUp = this.accordionContainer.querySelector('#extendedDescriptionArrowUp');
      state.extendedDescriptionExpanded = arrowUp && !arrowUp.classList.contains('hidden');
    }

    return state;
  }

  /**
   * Restore the accordion to its previous state
   * Reopens previously expanded panels and maintains extended description state
   * Only animates panels that actually need to change
   * 
   * @param {Object} state - The state object from getCurrentAccordionState()
   */
  restoreAccordionState(state) {
    const headers = this.accordionContainer.querySelectorAll('.accordion-header');
    let hasRestoredPanels = false;

    // Get the set of panel indices that should be open
    const panelsToOpen = new Set(state.expandedPanels);

    // If no panels were previously open, open the first one by default (control only)
    if (panelsToOpen.size === 0 && headers.length > 0 && this.#opensFirstPanelByDefault()) {
      panelsToOpen.add(0);
    }

    // Update each panel without unnecessary animations
    headers.forEach((header, index) => {
      const shouldBeOpen = panelsToOpen.has(index);
      const isCurrentlyOpen = header.getAttribute('aria-expanded') === 'true';

      // Only animate if state is changing
      if (shouldBeOpen !== isCurrentlyOpen) {
        header.setAttribute('aria-expanded', shouldBeOpen);
        this.updateAccordionHeight(header);
      }

      // Update icon state
      const plusIcon = header.querySelector('.accordion-icon-plus');
      const minusIcon = header.querySelector('.accordion-icon-minus');
      if (plusIcon && minusIcon) {
        if (shouldBeOpen) {
          plusIcon.classList.add('hidden');
          minusIcon.classList.remove('hidden');
        } else {
          plusIcon.classList.remove('hidden');
          minusIcon.classList.add('hidden');
        }
      }

      if (shouldBeOpen) hasRestoredPanels = true;
    });

    // Restore extended description state if it was expanded
    if (state.extendedDescriptionExpanded) {
      const extendedDescriptionToggle = this.accordionContainer.querySelector('#extendedDescriptionToggle');
      if (extendedDescriptionToggle) {
        // Simulate click to restore expanded state
        extendedDescriptionToggle.click();
      }
    }

    // Reinitialize heights of expanded panels after new content is laid out.
    // Without this, expanded panels can keep wrong height when variant content changes.
    this.reinitializeExpandedAccordionHeights();

    // When images or other async content load, content height can change; refresh then.
    this.scheduleAccordionHeightRefreshWhenContentLoads();
  }

  /**
   * Disconnect ResizeObserver and clear delayed refresh from previous variant update.
   */
  disconnectAccordionHeightRefresh() {
    if (this.accordionResizeObserver) {
      this.accordionResizeObserver.disconnect();
      this.accordionResizeObserver = null;
    }
    if (this.accordionImageLoadTimeout) {
      clearTimeout(this.accordionImageLoadTimeout);
      this.accordionImageLoadTimeout = null;
    }
  }

  /**
   * After accordion HTML is replaced: observe content size (e.g. when images load) and run
   * one delayed refresh so expanded panel heights stay correct.
   */
  scheduleAccordionHeightRefreshWhenContentLoads() {
    if (!this.accordionContainer) return;

    const refresh = () => this.reinitializeExpandedAccordionHeights();
    let debounce = null;
    const debouncedRefresh = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        refresh();
      }, 50);
    };

    const contentWrappers = this.accordionContainer.querySelectorAll('.accordion-content > div');
    if (contentWrappers.length && typeof ResizeObserver !== 'undefined') {
      this.accordionResizeObserver = new ResizeObserver(debouncedRefresh);
      contentWrappers.forEach((el) => this.accordionResizeObserver.observe(el));
    }
    this.accordionImageLoadTimeout = setTimeout(refresh, 300);
  }

  /**
   * Re-measure and set maxHeight for every currently expanded accordion panel.
   * Call after replacing accordion HTML (e.g. on variant change) so heights match new content.
   */
  reinitializeExpandedAccordionHeights() {
    const headers = this.accordionContainer.querySelectorAll('.accordion-header');
    const run = () => {
      headers.forEach((header, index) => {
        if (header.getAttribute('aria-expanded') !== 'true') return;
        let additionalHeight = 0;
        if (index === 0) {
          const fullContent = this.accordionContainer.querySelector('#extendedDescriptionFullContent');
          const fullEl = this.accordionContainer.querySelector('#extendedDescriptionFull');
          if (fullContent && fullEl && fullEl.style.maxHeight && fullEl.style.maxHeight !== '50px') {
            additionalHeight = fullContent.getBoundingClientRect().height;
          }
        }
        this.updateAccordionHeight(header, additionalHeight);
      });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }

  /**
   * Reinitialize all accordion functionality after HTML replacement
   * Rebinds all event listeners for accordion headers, extended description toggle,
   * and image grid lightboxes
   */
  reinitializeAccordion() {
    const accordionHeaders = this.accordionContainer.querySelectorAll('.accordion-header');
    const extendedDescriptionToggle = this.accordionContainer.querySelector('#extendedDescriptionToggle');
    const extendedDescriptionGradient = this.accordionContainer.querySelector('#extendedDescriptionGradient');
    const extendedDescriptionContent = this.accordionContainer.querySelector('#extendedDescriptionContent');
    const extendedDescriptionText = this.accordionContainer.querySelector('#extendedDescriptionText');
    const extendedDescriptionArrowDown = this.accordionContainer.querySelector('#extendedDescriptionArrowDown');
    const extendedDescriptionArrowUp = this.accordionContainer.querySelector('#extendedDescriptionArrowUp');
    const extendedDescriptionFull = this.accordionContainer.querySelector('#extendedDescriptionFull');
    const extendedDescriptionFullContent = this.accordionContainer.querySelector('#extendedDescriptionFullContent');

    let isExtendedDescriptionExpanded = false;

    // Remove existing event listeners by cloning headers
    accordionHeaders.forEach((header) => {
      const newHeader = header.cloneNode(true);
      header.parentNode.replaceChild(newHeader, header);
    });

    // Get fresh references after cloning and bind new event listeners
    const updatedHeaders = this.accordionContainer.querySelectorAll('.accordion-header');

    updatedHeaders.forEach((header) => {
      this.#bindAccordionHeaderClick(header);
    });

    // Rebind extended description toggle functionality
    if (extendedDescriptionToggle) {
      // Remove existing event listener by cloning
      const newToggle = extendedDescriptionToggle.cloneNode(true);
      extendedDescriptionToggle.parentNode.replaceChild(newToggle, extendedDescriptionToggle);

      // Get fresh references after cloning
      const freshExtendedDescriptionGradient = this.accordionContainer.querySelector('#extendedDescriptionGradient');
      const freshExtendedDescriptionContent = this.accordionContainer.querySelector('#extendedDescriptionContent');
      const freshExtendedDescriptionText = this.accordionContainer.querySelector('#extendedDescriptionText');
      const freshExtendedDescriptionArrowDown = this.accordionContainer.querySelector('#extendedDescriptionArrowDown');
      const freshExtendedDescriptionArrowUp = this.accordionContainer.querySelector('#extendedDescriptionArrowUp');
      const freshExtendedDescriptionFull = this.accordionContainer.querySelector('#extendedDescriptionFull');
      const freshExtendedDescriptionFullContent = this.accordionContainer.querySelector('#extendedDescriptionFullContent');

      newToggle.addEventListener('click', () => {
        const firstHeader = updatedHeaders[0];

        if (!isExtendedDescriptionExpanded) {
          // Expand: Show full content
          if (freshExtendedDescriptionGradient) {
            freshExtendedDescriptionGradient.style.opacity = '0';
            freshExtendedDescriptionGradient.style.pointerEvents = 'none';
          }

          if (freshExtendedDescriptionText) {
            freshExtendedDescriptionText.textContent = 'Weniger lesen';
          }
          if (freshExtendedDescriptionArrowDown) {
            freshExtendedDescriptionArrowDown.classList.add('hidden');
          }
          if (freshExtendedDescriptionArrowUp) {
            freshExtendedDescriptionArrowUp.classList.remove('hidden');
          }
          if (newToggle) {
            newToggle.style.bottom = '23px';
          }
          if (freshExtendedDescriptionFull && freshExtendedDescriptionFullContent) {
            const extendedHeight = freshExtendedDescriptionFullContent.getBoundingClientRect().height;
            freshExtendedDescriptionFull.style.maxHeight = extendedHeight + 'px';
          }

          isExtendedDescriptionExpanded = true;
        } else {
          // Collapse: First animate height down, then update content
          // Step 1: Animate gradient back in
          if (freshExtendedDescriptionGradient) {
            freshExtendedDescriptionGradient.style.opacity = '1';
            freshExtendedDescriptionGradient.style.pointerEvents = 'auto';
          }

          // Step 2: Update UI elements first
          if (freshExtendedDescriptionText) {
            freshExtendedDescriptionText.textContent = 'Weiter lesen';
          }
          if (freshExtendedDescriptionArrowDown) {
            freshExtendedDescriptionArrowDown.classList.remove('hidden');
          }
          if (freshExtendedDescriptionFull) {
            freshExtendedDescriptionFull.style.maxHeight = '50px';
          }
          if (newToggle) {
            newToggle.style.bottom = '35px';
          }
          if (freshExtendedDescriptionArrowUp) {
            freshExtendedDescriptionArrowUp.classList.add('hidden');
          }

          // Step 3: Wait for gradient animation, then update content and height
          setTimeout(() => {
            isExtendedDescriptionExpanded = false;
            // Recalculate height after content update
            if (firstHeader) {
              this.updateAccordionHeight(firstHeader);
            }
          }, 200); // Match the gradient transition duration

          // Return early to avoid immediate height recalculation
          return;
        }

        // Recalculate height after content update (only for expand case)
        if (firstHeader && freshExtendedDescriptionFullContent) {
          this.updateAccordionHeight(firstHeader, freshExtendedDescriptionFullContent.getBoundingClientRect().height);
        }
      });
    }

    // Reinitialize image grids
    this.reinitializeImageGrids();

    // Rebind lightbox events
    this.bindLightboxEvents();

    this.#closeAllPdpFaqDetails();
  }

  /**
   * Initialize accordion functionality on initial page load
   * Sets up all event listeners and initial state
   */
  initializeAccordionOnLoad() {
    const accordionHeaders = this.accordionContainer.querySelectorAll('.accordion-header');
    const extendedDescriptionToggle = this.accordionContainer.querySelector('#extendedDescriptionToggle');
    const extendedDescriptionGradient = this.accordionContainer.querySelector('#extendedDescriptionGradient');
    const extendedDescriptionContent = this.accordionContainer.querySelector('#extendedDescriptionContent');
    const extendedDescriptionText = this.accordionContainer.querySelector('#extendedDescriptionText');
    const extendedDescriptionArrowDown = this.accordionContainer.querySelector('#extendedDescriptionArrowDown');
    const extendedDescriptionArrowUp = this.accordionContainer.querySelector('#extendedDescriptionArrowUp');
    const extendedDescriptionFull = this.accordionContainer.querySelector('#extendedDescriptionFull');
    const extendedDescriptionFullContent = this.accordionContainer.querySelector('#extendedDescriptionFullContent');

    let isExtendedDescriptionExpanded = false;

    // Set up accordion header functionality
    accordionHeaders.forEach((header) => {
      this.#bindAccordionHeaderClick(header);
    });

    // Set up extended description toggle
    if (extendedDescriptionToggle) {
      extendedDescriptionToggle.addEventListener('click', () => {
        const firstHeader = accordionHeaders[0];

        if (!isExtendedDescriptionExpanded) {
          // Expand: Show full content
          if (extendedDescriptionGradient) {
            extendedDescriptionGradient.style.opacity = '0';
            extendedDescriptionGradient.style.pointerEvents = 'none';
          }

          if (extendedDescriptionText) {
            extendedDescriptionText.textContent = 'Weniger lesen';
          }
          if (extendedDescriptionArrowDown) {
            extendedDescriptionArrowDown.classList.add('hidden');
          }
          if (extendedDescriptionArrowUp) {
            extendedDescriptionArrowUp.classList.remove('hidden');
          }
          if (extendedDescriptionToggle) {
            extendedDescriptionToggle.style.bottom = '23px';
          }
          if (extendedDescriptionFull && extendedDescriptionFullContent) {
            const extendedHeight = extendedDescriptionFullContent.getBoundingClientRect().height;
            extendedDescriptionFull.style.maxHeight = extendedHeight + 'px';
          }

          isExtendedDescriptionExpanded = true;
        } else {
          // Collapse: First animate height down, then update content
          // Step 1: Animate gradient back in
          if (extendedDescriptionGradient) {
            extendedDescriptionGradient.style.opacity = '1';
            extendedDescriptionGradient.style.pointerEvents = 'auto';
          }

          // Step 2: Update UI elements first
          if (extendedDescriptionText) {
            extendedDescriptionText.textContent = 'Weiter lesen';
          }
          if (extendedDescriptionArrowDown) {
            extendedDescriptionArrowDown.classList.remove('hidden');
          }
          if (extendedDescriptionFull) {
            extendedDescriptionFull.style.maxHeight = '50px';
          }
          if (extendedDescriptionToggle) {
            extendedDescriptionToggle.style.bottom = '35px';
          }
          if (extendedDescriptionArrowUp) {
            extendedDescriptionArrowUp.classList.add('hidden');
          }

          // Step 3: Wait for gradient animation, then update content and height
          setTimeout(() => {
            isExtendedDescriptionExpanded = false;
            // Recalculate height after content update
            if (firstHeader) {
              this.updateAccordionHeight(firstHeader);
            }
          }, 200); // Match the gradient transition duration

          // Return early to avoid immediate height recalculation
          return;
        }

        // Recalculate height after content update (only for expand case)
        if (firstHeader && extendedDescriptionFullContent) {
          this.updateAccordionHeight(firstHeader, extendedDescriptionFullContent.getBoundingClientRect().height);
        }
      });
    }

    // Initialize image grids
    this.reinitializeImageGrids();

    // Bind lightbox events
    this.bindLightboxEvents();

    this.#closeAllPdpFaqDetails();

    accordionHeaders.forEach((header, index) => {
      if (index === 0) return;

      const content = header.nextElementSibling;
      if (content?.classList.contains('accordion-content')) {
        content.style.maxHeight = '0';
      }
    });

    if (accordionHeaders.length > 0 && this.#opensFirstPanelByDefault()) {
      const firstHeader = accordionHeaders[0];
      firstHeader.setAttribute('aria-expanded', 'true');
      const firstPlusIcon = firstHeader.querySelector('.accordion-icon-plus');
      const firstMinusIcon = firstHeader.querySelector('.accordion-icon-minus');
      if (firstPlusIcon && firstMinusIcon) {
        firstPlusIcon.classList.add('hidden');
        firstMinusIcon.classList.remove('hidden');
      }
      this.updateAccordionHeight(firstHeader);
    }
  }

  #opensFirstPanelByDefault() {
    return this.accordionContainer?.dataset.defaultOpen !== 'false';
  }

  /**
   * Find and reinitialize all image grids within the accordion
   * Searches for [data-image-grid] elements and reinitializes their lightbox functionality
   */
  reinitializeImageGrids() {
    // Find all image grids within the accordion container
    const imageGrids = this.accordionContainer.querySelectorAll('[data-image-grid]');

    imageGrids.forEach((grid) => {
      this.initializeImageGrid(grid);
    });
  }

  /**
   * Initialize lightbox functionality for a single image grid
   * Recreates the complete functionality from ek-image-grid.liquid including:
   * - Lightbox open/close/navigation
   * - Panzoom integration for image zooming
   * - Keyboard navigation support
   * - Custom event dispatching for accordion height updates
   * 
   * @param {HTMLElement} grid - The image grid container element
   */
  initializeImageGrid(grid) {
    const triggers = grid.querySelectorAll('[data-lightbox-trigger]');
    const gridView = grid.querySelector('[data-grid-view]');
    let lightboxView = grid.querySelector('[data-lightbox-view]');
    const closeBtn = grid.querySelector('[data-lightbox-close]');
    const prevBtn = grid.querySelector('[data-lightbox-prev]');
    const nextBtn = grid.querySelector('[data-lightbox-next]');
    const images = grid.querySelectorAll('[data-lightbox-image]');
    const currentSpan = grid.querySelector('[data-lightbox-current]');
    const altText = grid.querySelector('[data-lightbox-alt]');

    let currentIndex = 0;
    const totalImages = images.length;
    let isLightboxOpen = false;
    let panzoomInstances = {};

    // Store alt texts for accessibility
    const altTexts = Array.from(images).map((img) => {
      const imgElement = img.querySelector('img');
      return imgElement ? imgElement.alt : 'Produktbild';
    });

    /**
     * Initialize Panzoom for each image in the lightbox
     * Provides zoom and pan functionality with touch and mouse support
     */
    function initializePanzoom() {
      const currentImages = lightboxView.querySelectorAll('[data-lightbox-image]');
      currentImages.forEach((imageContainer, index) => {
        const panzoomElement = imageContainer.querySelector('.ek-panzoom');
        if (panzoomElement && typeof Panzoom !== 'undefined') {
          panzoomInstances[index] = new Panzoom(panzoomElement, {
            panOnlyZoomed: true,
            bounce: false,
            rubberband: false,
            wheel: true,
            maxScale: 3,
            minScale: 1,
            step: 0.1,
            cursor: 'zoom-in',
            on: {
              afterTransform: (panzoomInstance) => {
                if (panzoomInstance.scale > 1) {
                  panzoomElement.style.cursor = 'grab';
                } else {
                  panzoomElement.style.cursor = 'zoom-in';
                }
              },
              startPan: (panzoomInstance) => {
                panzoomElement.style.cursor = 'grabbing';
              },
              endPan: (panzoomInstance) => {
                if (panzoomInstance.scale > 1) {
                  panzoomElement.style.cursor = 'grab';
                } else {
                  panzoomElement.style.cursor = 'zoom-in';
                }
              }
            }
          });
        }
      });
    }

    /**
     * Show a specific image in the lightbox
     * Hides all other images and updates navigation/accessibility info
     * 
     * @param {number} index - The index of the image to show
     */
    function showImage(index) {
      // Get current images from lightbox
      const currentImages = lightboxView.querySelectorAll('[data-lightbox-image]');

      // Hide all images
      currentImages.forEach((img) => img.classList.add('hidden'));

      // Show current image
      if (currentImages[index]) {
        currentImages[index].classList.remove('hidden');
        currentIndex = index;

        // Reset previous image zoom if it exists
        Object.keys(panzoomInstances).forEach(key => {
          if (parseInt(key) !== index && panzoomInstances[key]) {
            panzoomInstances[key].reset();
          }
        });

        // Update counter
        if (currentSpan) {
          currentSpan.textContent = index + 1;
        }

        // Update alt text
        if (altText) {
          altText.textContent = altTexts[index] || 'Produktbild';
        }
      }
    }

    /**
     * Open the lightbox and show a specific image
     * Initializes panzoom if needed and dispatches events for accordion height updates
     * 
     * @param {number} index - The index of the image to show initially
     */
    function openLightbox(index = 0) {
      isLightboxOpen = true;
      gridView.classList.add('hidden');
      lightboxView.classList.remove('hidden');

      // Initialize Panzoom if not already done
      if (Object.keys(panzoomInstances).length === 0) {
        initializePanzoom();
      }

      showImage(index);

      // Emit event for accordion height adjustment
      const lightboxOpenEvent = new CustomEvent('imageLightboxOpen', {
        detail: { gridElement: grid },
        bubbles: true,
      });
      grid.dispatchEvent(lightboxOpenEvent);
    }

    /**
     * Close the lightbox and return to grid view
     * Resets all zoom states and dispatches events for accordion height updates
     */
    function closeLightbox() {
      isLightboxOpen = false;
      lightboxView.classList.add('hidden');
      gridView.classList.remove('hidden');

      // Reset all zoom states
      Object.values(panzoomInstances).forEach(panzoom => {
        if (panzoom) {
          panzoom.reset();
        }
      });

      // Emit event for accordion height adjustment
      const lightboxCloseEvent = new CustomEvent('imageLightboxClose', {
        detail: { gridElement: grid },
        bubbles: true,
      });
      grid.dispatchEvent(lightboxCloseEvent);
    }

    /**
     * Navigate to the next image in the lightbox
     */
    function nextImage() {
      const newIndex = (currentIndex + 1) % totalImages;
      showImage(newIndex);
    }

    /**
     * Navigate to the previous image in the lightbox
     */
    function prevImage() {
      const newIndex = (currentIndex - 1 + totalImages) % totalImages;
      showImage(newIndex);
    }

    // Bind click events to image triggers (remove old listeners by cloning)
    triggers.forEach((trigger) => {
      const newTrigger = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(newTrigger, trigger);

      newTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(newTrigger.getAttribute('data-image-index'));
        openLightbox(index);
      });
    });

    // Bind close button event
    if (closeBtn) {
      const newCloseBtn = closeBtn.cloneNode(true);
      closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
      newCloseBtn.addEventListener('click', closeLightbox);
    }

    // Bind navigation button events
    if (prevBtn) {
      const newPrevBtn = prevBtn.cloneNode(true);
      prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
      newPrevBtn.addEventListener('click', prevImage);
    }

    if (nextBtn) {
      const newNextBtn = nextBtn.cloneNode(true);
      nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
      newNextBtn.addEventListener('click', nextImage);
    }

    // Click outside to close lightbox - must be done after cloning
    if (lightboxView) {
      lightboxView.addEventListener('click', (e) => {
        // Check if the click was on the lightbox container itself (not on the image or controls)
        if (e.target === lightboxView || e.target.closest('.lightbox-image-container') === e.target) {
          closeLightbox();
        }
      });

      // Prevent clicks on the image itself from closing the lightbox
      const imageContainers = lightboxView.querySelectorAll('.lightbox-image');
      imageContainers.forEach((container) => {
        container.addEventListener('click', (e) => {
          // Only prevent if clicking on the image or panzoom container, not the background
          const clickedElement = e.target;
          if (clickedElement.tagName === 'IMG' ||
            clickedElement.classList.contains('ek-panzoom') ||
            clickedElement.closest('.ek-panzoom')) {
            e.stopPropagation();
          }
        });
      });
    }

    // Store functions on grid element for external access
    grid.ekImageGridData = {
      closeLightbox: closeLightbox,
      isLightboxOpen: () => isLightboxOpen
    };

    // Keyboard navigation support
    const keydownHandler = (e) => {
      if (!isLightboxOpen) return;

      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          if (totalImages > 1) prevImage();
          break;
        case 'ArrowRight':
          if (totalImages > 1) nextImage();
          break;
      }
    };

    // Clean up existing keyboard listeners and add new one
    document.removeEventListener('keydown', keydownHandler);
    document.addEventListener('keydown', keydownHandler);
  }

  #resetPdpFaqDetails(header) {
    const content = header.nextElementSibling;
    if (!content?.classList.contains('accordion-content')) return;

    for (const detail of content.querySelectorAll('.pdp-faq-accordion details')) {
      detail.open = false;
    }
  }

  #closeAllPdpFaqDetails() {
    if (!this.accordionContainer) return;

    for (const detail of this.accordionContainer.querySelectorAll('.pdp-faq-accordion details')) {
      detail.open = false;
    }
  }

  #headerHasPdpFaq(header) {
    const content = header.nextElementSibling;
    return Boolean(content?.querySelector('.pdp-faq-accordion'));
  }

  #bindAccordionHeaderClick(header) {
    header.addEventListener('click', () => {
      const isExpanded = header.getAttribute('aria-expanded') === 'true';
      const willExpand = !isExpanded;
      const hasPdpFaq = this.#headerHasPdpFaq(header);

      if (willExpand && hasPdpFaq) {
        this.#resetPdpFaqDetails(header);
      }

      header.setAttribute('aria-expanded', willExpand);

      if (!willExpand && hasPdpFaq) {
        this.#resetPdpFaqDetails(header);
      }

      this.updateAccordionHeight(header);

      if (willExpand) {
        setTimeout(() => {
          const decorationElement = document.getElementById('accordion-decoration');
          if (decorationElement) {
            const elementRect = header.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = scrollTop + elementRect.top - 40;
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    });
  }

  #updatePdpFaqPanelHeight(header, isExpanded) {
    const content = header.nextElementSibling;
    if (!content?.classList.contains('accordion-content')) return;

    this.#cancelAccordionHeightFrame(content);

    const plusIcon = header.querySelector('.accordion-icon-plus');
    const minusIcon = header.querySelector('.accordion-icon-minus');

    if (isExpanded) {
      content.style.maxHeight = 'none';
      if (plusIcon) plusIcon.classList.add('hidden');
      if (minusIcon) minusIcon.classList.remove('hidden');
      return;
    }

    const currentHeight = content.scrollHeight;
    content.style.maxHeight = `${currentHeight}px`;
    content.offsetHeight;
    requestAnimationFrame(() => {
      content.style.maxHeight = '0';
    });
    if (plusIcon) plusIcon.classList.remove('hidden');
    if (minusIcon) minusIcon.classList.add('hidden');
  }

  #cancelAccordionHeightFrame(content) {
    const frameId = this.accordionHeightFrameIds.get(content);
    if (frameId) {
      cancelAnimationFrame(frameId);
      this.accordionHeightFrameIds.delete(content);
    }
  }

  #measureAccordionContentHeight(content, additionalHeight = 0) {
    const previousMaxHeight = content.style.maxHeight;
    content.style.maxHeight = 'none';
    const height = content.scrollHeight + additionalHeight;
    content.style.maxHeight = previousMaxHeight;
    return height;
  }

  /**
   * Update the height of an accordion panel
   * Calculates the content height and animates the panel open/closed
   * Also manages the plus/minus icon visibility
   * 
   * @param {HTMLElement} header - The accordion header element
   */
  updateAccordionHeight(header, additionalHeight = 0) {
    if (!header) return;

    const isExpanded = header.getAttribute('aria-expanded') === 'true';

    if (this.#headerHasPdpFaq(header)) {
      this.#updatePdpFaqPanelHeight(header, isExpanded);
      return;
    }

    const content = header.nextElementSibling;

    if (!content || !content.classList.contains('accordion-content')) {
      console.warn('Accordion content element not found for header:', header);
      return;
    }

    this.#cancelAccordionHeightFrame(content);

    const plusIcon = header.querySelector('.accordion-icon-plus');
    const minusIcon = header.querySelector('.accordion-icon-minus');

    if (isExpanded) {
      const height = this.#measureAccordionContentHeight(content, additionalHeight);

      const frameId = requestAnimationFrame(() => {
        this.accordionHeightFrameIds.delete(content);
        if (header.getAttribute('aria-expanded') !== 'true') return;

        content.style.maxHeight = `${height}px`;
        if (plusIcon) plusIcon.classList.add('hidden');
        if (minusIcon) minusIcon.classList.remove('hidden');
      });
      this.accordionHeightFrameIds.set(content, frameId);
    } else {
      content.style.maxHeight = '0';
      if (plusIcon) plusIcon.classList.remove('hidden');
      if (minusIcon) minusIcon.classList.add('hidden');
    }
  }

  /**
   * Get the full extended description content
   * Fetches the full description from the variant data
   * 
   * @returns {string} The full description content
   */
  getFullExtendedDescription() {
    // Get the full description from the data attribute that was set in the Liquid template
    const content = this.accordionContainer.querySelector('#extendedDescriptionContent');
    if (!content) return '';

    // Get the full description from the data attribute
    const fullDescription = content.getAttribute('data-full-description');
    if (fullDescription) {
      return fullDescription;
    }

    // Fallback: try to get it from the content itself
    const contentP = content.querySelector('p');
    return contentP ? contentP.innerHTML : '';
  }

  /**
   * Bind lightbox events for accordion height adjustment
   * Listens for custom lightbox open/close events and adjusts accordion height accordingly
   * This ensures the accordion container adapts when lightboxes are opened/closed
   */
  bindLightboxEvents() {
    // Handle lightbox open events
    document.addEventListener('imageLightboxOpen', (event) => {
      const imageGrid = event.detail.gridElement;
      const accordionPanel = imageGrid.closest('.accordion-content');

      if (accordionPanel && accordionPanel.closest('#accordion-container') === this.accordionContainer) {
        const accordionHeader = accordionPanel.previousElementSibling;
        const isExpanded = accordionHeader.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          setTimeout(() => {
            this.updateAccordionHeight(accordionHeader);
          }, 50);
        }
      }
    });

    // Handle lightbox close events
    document.addEventListener('imageLightboxClose', (event) => {
      const imageGrid = event.detail.gridElement;
      const accordionPanel = imageGrid.closest('.accordion-content');

      if (accordionPanel && accordionPanel.closest('#accordion-container') === this.accordionContainer) {
        const accordionHeader = accordionPanel.previousElementSibling;
        const isExpanded = accordionHeader.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          setTimeout(() => {
            this.updateAccordionHeight(accordionHeader);
          }, 50);
        }
      }
    });
  }

}

// Initialize the accordion variant updater when the script loads
window.accordionVariantUpdater = new AccordionVariantUpdater();

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AccordionVariantUpdater;
}
